import { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { customerApiService, SignupCodeResponse } from 'services/api';

interface SignupCodeData {
  codes: SignupCodeResponse[];
  loading: boolean;
  error: string | null;
}

export const useSignupCodes = () => {
  const { user, company } = useAuth();
  const [signupCodeData, setSignupCodeData] = useState<SignupCodeData>({
    codes: [],
    loading: true,
    error: null
  });

  const fetchSignupCodes = async () => {
    if (!user || !company) {
      setSignupCodeData(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setSignupCodeData(prev => ({ ...prev, loading: true, error: null }));

      const response = await customerApiService.listSignupCodes();

      if (response.success && response.data) {
        setSignupCodeData({
          codes: response.data,
          loading: false,
          error: null
        });
      } else {
        throw new Error(response.message || 'Failed to fetch signup codes');
      }
    } catch (error) {
      console.error('Error fetching signup codes:', error);
      setSignupCodeData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch signup codes'
      }));
    }
  };

  const generateSignupCode = async (expiresInDays: number = 30, maxUses: number = 10) => {
    try {
      const response = await customerApiService.generateSignupCode(expiresInDays, maxUses);

      if (response.success) {
        await fetchSignupCodes(); // Refresh the list
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to generate signup code');
      }
    } catch (error) {
      console.error('Error generating signup code:', error);
      throw error;
    }
  };

  const revokeSignupCode = async (codeId: string) => {
    try {
      const response = await customerApiService.revokeSignupCode(codeId);

      if (response.success) {
        await fetchSignupCodes(); // Refresh the list
        return { success: true };
      } else {
        throw new Error(response.message || 'Failed to revoke signup code');
      }
    } catch (error) {
      console.error('Error revoking signup code:', error);
      throw error;
    }
  };

  const deleteSignupCode = async (codeId: string) => {
    try {
      const response = await customerApiService.deleteSignupCode(codeId);

      if (response.success) {
        await fetchSignupCodes();
        return { success: true };
      } else {
        throw new Error(response.message || 'Failed to delete signup code');
      }
    } catch (error) {
      console.error('Error deleting signup code:', error);
      throw error;
    }
  };

  const refreshData = () => {
    fetchSignupCodes();
  };

  useEffect(() => {
    fetchSignupCodes();
  }, [user, company]);

  return {
    ...signupCodeData,
    generateSignupCode,
    revokeSignupCode,
    deleteSignupCode,
    refreshData
  };
};
